import { GlossaryListResponse } from '@aiax-hub/contracts';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../i18n';
import { termOf, type GlossaryTerm } from './index';

/** The same cache as the glossary screen, so edits invalidate every open explanation. */
export function useGlossaryTerm(slug: string): GlossaryTerm | null {
  const { me } = useAuth();
  const lang = useLang();
  const query = useQuery({
    queryKey: ['glossary', 'list'],
    queryFn: ({ signal }) => apiFetch('/api/v1/glossary', { schema: GlossaryListResponse, signal, allow401: true }),
    enabled: !!me && !me.gates.length,
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (!query.data) return termOf(slug);
  const g = query.data.terms.find((term) => term.slug === slug || term.oldSlugs.includes(slug));
  if (!g || g.hidden) return null;
  const link = g.links[lang] ?? g.links.ko ?? g.links.en;
  return {
    slug: g.slug, term: g.term.ko, termEn: g.term.en, plainKo: g.plain.ko, plainEn: g.plain.en,
    def: g.def, example: g.example ?? undefined, category: g.category, core: g.core,
    aliases: g.aliases, oldSlugs: g.oldSlugs, rawNames: g.rawNames, related: g.related,
    url: link?.url ?? null, urlLabel: link?.sourceName ?? null, docLang: link?.docLang,
    linkStatus: link?.status, manualRef: g.manualRef ?? undefined, labelKey: g.labelKey ?? undefined,
    screens: g.screens,
  };
}
